import LBox from "@components/LBox";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { theme } from "@theme";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParse from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import { isNumber } from "lodash";
import * as React from "react";
import { ScrollView } from "react-native-gesture-handler";
import SkeletonPlaceholder from "react-native-skeleton-placeholder";
import { Path, Svg } from "react-native-svg";
import type { Stats } from "@libs/types/stats";

dayjs.extend(customParse);
dayjs.extend(advancedFormat);
dayjs.extend(utc);

const BAR_SIZE = 44;
const LEFT_OFFSET = 22;
const GRAPH_HEIGHT = 200;

const StatsSkeleton = () => (
  <SkeletonPlaceholder
    highlightColor="rgba(255,255,255,1)"
    backgroundColor="rgba(255,255,255,0.3)"
    speed={1200}
  >
    <SkeletonPlaceholder.Item alignItems="center" gap={30}>
      <SkeletonPlaceholder.Item width="100%" aspectRatio={1} borderRadius={5} />
    </SkeletonPlaceholder.Item>
  </SkeletonPlaceholder>
);

type StatsStreamsProps = {
  stats: Stats["Revenue"]["Output"]["data"]["by_date"] | undefined;
  month: dayjs.Dayjs;
};

const StatsStreams = ({ stats, month }: StatsStreamsProps) => {
  const [selected, setSelected] = React.useState<
    "stream_time" | "streams" | "listeners"
  >("stream_time");

  const byDay = stats;

  const { graphData, allDaysInMonth, yAxisLabels, max, color } =
    React.useMemo(() => {
      const numberOfDaysInMonth = dayjs(month).daysInMonth();
      type ByDayValue = NonNullable<StatsStreamsProps["stats"]>[string];
      const getSelectedValue = (value: ByDayValue): number => {
        if (selected === "stream_time") return value.stream_time;
        if (selected === "listeners") return value.number_of_users;
        return value.number_of_streams;
      };

      const rawData = (Object.entries(byDay || {}) as [string, ByDayValue][])
        .sort(([dateStringA], [dateStringB]) => {
          return (
            dayjs(dateStringA, "DD-MM-YYYY").unix() -
            dayjs(dateStringB, "DD-MM-YYYY").unix()
          );
        })
        .map(([dateString, value]) => {
          return {
            value: getSelectedValue(value),
            date: dayjs(dateString, "DD-MM-YYYY").toDate(),
          };
        });

      const max = rawData.reduce((currentMax, item) => {
        return item.value > currentMax ? item.value : currentMax;
      }, 0);

      // fill up data with empty values for days that have no data
      // let data = rawData;
      const data = new Array(numberOfDaysInMonth).fill(0).map((_, index) => {
        const date = dayjs(month.startOf("month")).add(index, "day").toDate();
        const existingData = rawData.find(
          (item) => item.date.getDate() === date.getDate(),
        );
        if (existingData) {
          return existingData;
        }
        return {
          value: 0,
          date,
        };
      });

      //if the first value in data is not the first day of the month, fill in from the first of the month until the first value with 0s
      if (
        data.length &&
        data?.at(0)?.date &&
        data?.at(0)?.date.getTime() !==
          month.startOf("month").toDate().getTime()
      ) {
        for (let i = data[0].date.getDate() - 2; i >= 0; i--) {
          const date = dayjs(month.startOf("month"))
            .subtract(-i, "day")
            .toDate();
          data.unshift({
            value: 0,
            date,
          });
        }
      }

      const isCurrentMonth =
        dayjs().format("YYYY-MM") === month.format("YYYY-MM");
      const allDaysInMonth = [];
      for (
        let i = 0;
        i < (isCurrentMonth ? month.date() : month.daysInMonth());
        i++
      ) {
        allDaysInMonth.push(month.utc().startOf("month").add(i, "day"));
      }

      const yAxisLabels = [];
      for (let i = 0; i <= 5; i += 1) {
        yAxisLabels.push(((max / 5) * i).toFixed(2));
      }

      let color: keyof typeof theme.colors;
      switch (selected) {
        case "stream_time":
          color = "barChartBarTop";
          break;
        case "listeners":
          color = "barChartBarTop2";
          break;
        case "streams":
          color = "barChartBarTop3";
          break;
      }

      return {
        graphData: data,
        allDaysInMonth,
        yAxisLabels,
        max,
        color: color,
      };
    }, [byDay, month, selected]);

  const scrollRef = React.useRef<ScrollView>(null);

  function buildPath(
    graphData: { value: number; date: Date }[],
  ): string | undefined {
    if (graphData.length === 0) return "";

    let path = ``;
    for (let i = 0; i <= graphData.length - 1; i++) {
      const val = graphData[i].value;
      const x = LEFT_OFFSET + LEFT_OFFSET * 2 * i;
      const y = max === 0 ? 0 : GRAPH_HEIGHT * (1 - val / max);
      if (!isNumber(x) || !isNumber(y)) {
        return undefined;
      }
      if (i === 0) {
        path += `M${x},${y} `;
      } else {
        path += `L${x},${y} `;
      }
    }
    return path;
  }

  const path = buildPath(graphData);

  if (!stats) return <StatsSkeleton />;

  return (
    <LBox>
      <LText variant="h3" textAlign="center" color="inboxDate">
        Your songs daily
      </LText>
      {graphData.length > 0 ? (
        <LBox my="m1">
          <LBox mb="s2">
            <LBox
              position={"absolute"}
              top={0}
              left={0}
              right={0}
              bottom={20}
              alignItems="flex-end"
              borderBottomColor={"chartLine"}
              borderBottomWidth={0.5}
            >
              {yAxisLabels
                .slice()
                .reverse()
                .map((val, i) => {
                  if (i === yAxisLabels.length - 1) return null;
                  return (
                    <LBox
                      key={i}
                      flex={1}
                      width="100%"
                      borderTopColor="chartLine"
                      borderTopWidth={0.5}
                    >
                      <LText variant="bodyS">{val}</LText>
                    </LBox>
                  );
                })}
            </LBox>
            <ScrollView
              ref={scrollRef}
              onLayout={() => {
                if (scrollRef.current) {
                  const whereTo = BAR_SIZE * ((graphData?.length ?? 1) - 1);
                  scrollRef.current.scrollTo({ x: whereTo, animated: false });
                }
              }}
              horizontal
              bounces={false}
              showsVerticalScrollIndicator={false}
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{
                flexGrow: 1,
              }}
            >
              <LBox
                position={"absolute"}
                top={0}
                left={0}
                right={0}
                bottom={2.5}
                flexDirection={"row"}
                alignItems="flex-end"
              >
                {allDaysInMonth.map((day) => {
                  return (
                    <LBox key={day.format("DD-MM-YYYY")} width={BAR_SIZE}>
                      <LText
                        variant="bodyS"
                        textAlign="center"
                        style={{
                          transform: [{ rotate: "-45deg" }],
                        }}
                      >
                        {day.format("Do")}
                      </LText>
                    </LBox>
                  );
                })}
              </LBox>
              <Svg
                style={{
                  height: GRAPH_HEIGHT,
                  width: (allDaysInMonth.length - 1) * BAR_SIZE,
                  marginBottom: 20,
                }}
                viewBox={`0 0 ${
                  (allDaysInMonth.length - 1) * BAR_SIZE
                } ${GRAPH_HEIGHT}`}
                preserveAspectRatio="xMinYMin meet"
              >
                <Path
                  d={path}
                  stroke={theme.colors[color]}
                  strokeWidth={3}
                  fill="transparent"
                />
              </Svg>
            </ScrollView>
          </LBox>
          <LBox flexDirection="row" justifyContent="space-evenly" mt="m1">
            <LPressable
              sentryEventName="stats_select_stream_time"
              onPress={() => setSelected("stream_time")}
            >
              <LBox flexDirection="row" alignItems="center">
                <LBox
                  width={10}
                  height={10}
                  borderRadius="max"
                  backgroundColor={["barChartBarBottom", "barChartBarTop"]}
                  mr="s1"
                />
                <LText
                  variant="bodyS"
                  color={selected === "stream_time" ? "default" : "chartLine"}
                >
                  Stream time
                </LText>
              </LBox>
            </LPressable>
            <LPressable
              sentryEventName="stats_select_listeners"
              onPress={() => setSelected("listeners")}
            >
              <LBox flexDirection="row" alignItems="center">
                <LBox
                  width={10}
                  height={10}
                  borderRadius="max"
                  backgroundColor={["barChartBarBottom2", "barChartBarTop2"]}
                  mr="s1"
                />
                <LText
                  variant="bodyS"
                  color={selected === "listeners" ? "default" : "chartLine"}
                >
                  Listeners
                </LText>
              </LBox>
            </LPressable>
            <LPressable
              sentryEventName="stats_select_streams"
              onPress={() => setSelected("streams")}
            >
              <LBox flexDirection="row" alignItems="center">
                <LBox
                  width={10}
                  height={10}
                  borderRadius="max"
                  backgroundColor={["barChartBarBottom3", "barChartBarTop3"]}
                  mr="s1"
                />
                <LText
                  variant="bodyS"
                  color={selected === "streams" ? "default" : "chartLine"}
                >
                  Streams
                </LText>
              </LBox>
            </LPressable>
          </LBox>
        </LBox>
      ) : (
        <LBox justifyContent="center" alignItems="center" mt="m1">
          <LText variant={"bodyM"}>No data yet...</LText>
        </LBox>
      )}
    </LBox>
  );
};

export default StatsStreams;
